import { DeleteOutlined, DownloadOutlined, InboxOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { App, Button, Card, Form, Input, Tooltip, Upload, UploadFile, UploadProps } from 'antd';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { removeOfferComment, updateOfferComment } from '../../../../../contextRedux/Offer/OfferModel';
import OfferService from '../../../../../services/offer/OfferService';
import { RootState } from '../../../../../store';
import request from '../../../../../utils/request';
import { saveBlobCrossPlatform } from '../../../../../utils/saveBlobCrossPlatform';
import './CommentsTab.css';

const { TextArea } = Input;
const { Dragger } = Upload;

const CommentsTab: React.FC<any> = () => {
  const dispatch = useDispatch();
  const offerComments = useSelector((state: RootState) => state.models.offer.offerCommentsTab);

  const departments = ['general', 'contabilitate', 'manager', 'instalatori'];
  const { message } = App.useApp();

  const uploadFiles = async (files: File[]): Promise<any[]> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    try {
      const response = await request(
        `/offer/upload`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('authToken'),
          },
          body: formData,
        },
        true
      );

      const data = await response.json();
      if (data?.errors?.length > 0) {
        message.error(data?.errors[0]);
        return [];
      } else {
        message.success(`File uploaded successfully!`);
        return data?.response;
      }
    } catch (error: any) {
      message.error(`Failed to upload files: ${error.message}`);
      return [];
    }
  };

  const { mutate: donwloadFile } = useMutation({
    mutationFn: (formData: any) => OfferService.downloadFile(formData),
    onSuccess: (response: any) => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'file.pdf';

      if (contentDisposition) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      return response.blob().then((blob: any) => {
        saveBlobCrossPlatform(blob, filename);
      });
    },
    onError: (error: any) => {
      message.error('Eroare procesare request: ' + error.message);
    },
  });

  const handleDownloadFile = async (file: UploadFile) => {
    // Search through offerComments to find the matching file
    let foundFile = null;

    for (const comment of offerComments) {
      if (comment.offerCommentFiles) {
        const matchingFile = comment.offerCommentFiles.find((pocf: any) => pocf.fileName === file.name);
        if (matchingFile) {
          foundFile = matchingFile;
          break;
        }
      }
    }

    if (foundFile) {
      const formData = new FormData();
      formData.append('fileName', foundFile.fileName);
      formData.append('fileData', foundFile.filePath);
      formData.append('fileType', foundFile.fileExtension);

      // Call your download function
      donwloadFile(formData);
    } else {
      message.error('File not found.');
    }
  };

  return (
    <Form>
      {departments.map((departmentName) => {
        const departmentComment = offerComments.find((comment: any) => comment.departmentName === departmentName) || {};

        const messageValue = departmentComment.message || '';

        const fileList: UploadFile[] =
          departmentComment.offerCommentFiles?.map((file: any) => ({
            uid: file.uid || file.id,
            name: file.fileName,
            status: 'done',
            url: file.filePath,
          })) || [];

        const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
          const newMessage = e.target.value;
          dispatch(
            updateOfferComment({
              departmentName,
              updates: { message: newMessage },
            })
          );
        };

        const uploadProps: UploadProps = {
          multiple: true,
          showUploadList: true,
          fileList: fileList,
          customRequest: async ({ onSuccess, onError, file }: any) => {
            try {
              const uploadedFiles = await uploadFiles([file]);
              if (uploadedFiles.length > 0) {
                const uploadedFile = { ...uploadedFiles[0], uid: file.uid };

                const updatedFiles = [...(departmentComment.offerCommentFiles || []), uploadedFile];

                dispatch(
                  updateOfferComment({
                    departmentName,
                    updates: { offerCommentFiles: updatedFiles },
                  })
                );
                onSuccess('ok');
              } else {
                onError('File upload failed');
              }
            } catch (error) {
              onError(error);
            }
          },
          beforeUpload: (file: any) => {
            const isAllowedType = [
              'image/jpg',
              'image/jpeg',
              'image/png',
              'application/pdf',
              'application/msword',
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            ].includes(file.type);
            if (!isAllowedType) {
              message.error('You can only upload JPG/PNG images or PDF/DOC files!');
              return Upload.LIST_IGNORE;
            }
            return true;
          },
          itemRender: (_originNode: React.ReactNode, file: UploadFile) => {
            return (
              <div className='file-actions'>
                <>
                  <div className='download-action-button'>
                    <Button type='link' icon={<DownloadOutlined />} onClick={() => handleDownloadFile(file)}>
                      {file.name}
                    </Button>
                  </div>

                  <Tooltip title='Remove'>
                    <DeleteOutlined
                      className='remove-action-icon'
                      onClick={() =>
                        dispatch(
                          removeOfferComment({
                            departmentName,
                            fileUid: file.uid as string,
                          })
                        )
                      }
                    />
                  </Tooltip>
                </>
              </div>
            );
          },
        };

        return (
          <Card key={departmentName} title={capitalize(departmentName)} className='comment-card'>
            <TextArea rows={4} placeholder='Add comments' value={messageValue} onChange={handleTextChange} />
            <Dragger {...uploadProps} style={{ marginTop: 10 }}>
              <p className='ant-upload-drag-icon'>
                <InboxOutlined />
              </p>
              <p className='ant-upload-text'>Click or drag files to this area to upload</p>
              <p className='ant-upload-hint'>Supports JPG, PNG, PDF, DOC files.</p>
            </Dragger>
          </Card>
        );
      })}
    </Form>
  );
};

function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export default CommentsTab;
